import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import html2pdf from 'html2pdf.js'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {Message} from 'primereact/message'
import {Sidebar} from 'primereact/sidebar'
import {useCallback, useMemo, useRef} from 'react'
import {useLocation} from 'react-router-dom/cjs/react-router-dom'
import EditColumn from '../../../assets/menu/ColumnEdit.svg'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../../utils/BlockScrollsUtils'
import {getUserId} from '../../authentication/AuthUtils'
import FormHeader from '../../form/FormHeader'
import PageContentWrapper from '../../layout/PageContentWrapper'
import ToolBoxWrapper from '../../layout/ToolBoxWrapper'
import {QueryTablePatientKey} from '../../new-table/NewTable'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import {
    accommodationStatusList,
    diagnosticList,
    identityWithHeldReasonList,
    materialStatusList,
    patientKeys,
    religionList,
    titleList,
} from '../../patient/PatientConstants'
import PdfContent from '../../reporting/PdfContent'
import {convertKeyToLabel} from '../TableUtils'
import {createExportLog} from './ExportEndPoint'

const types = {
    csv: 'CSV',
    pdf: 'PDF',
    excel: 'Excel',
}

const ExportForm = ({
    dataList = [],
    advancedOptions = [],
    queryTableKey = '',
    columnList = [],
    auditLog = false,
}) => {
    useSignals()
    const visible = useSignal(false)
    const dataTableRef = useRef(null)
    const {
        translateDatatable,
        translateCommons,
        translateOptions,
        translateKeys,
    } = TranslateUtils()
    const excludeIdxRows = useSignal([])

    const dataListWithIdx = useMemo(() => {
        if (!!dataList && dataList?.length > 0) {
            return dataList.map((item, idx) => {
                return {
                    index: idx,
                    ...item,
                }
            })
        }
        return []
    }, [dataList])

    const convertDataToExport = (key, value) => {
        let content = '-'

        if (!value) {
            return '-'
        }

        switch (key) {
            case patientKeys.title:
                content =
                    titleList.find(title => title.value === value)?.label || '-'
                break
            case patientKeys.gender || 'type':
                content = convertKeyToLabel(value)
                break
            case patientKeys.identityWithHeldReason:
                content =
                    translateOptions(identityWithHeldReasonList).find(
                        identity => identity.value === value,
                    ).label || '-'
                break
            case patientKeys.religion:
                content =
                    translateOptions(religionList).find(
                        religion => religion.value === value,
                    ).label || '-'
                break
            case patientKeys.maritalStatus:
                content =
                    translateOptions(materialStatusList).find(
                        marital => marital.value === value,
                    ).label || '-'
                break
            case patientKeys.accommodationStatus:
                content =
                    translateOptions(accommodationStatusList).find(
                        accommodation => accommodation.value === value,
                    ).label || '-'
                break
            case patientKeys.diagnostic:
                content =
                    translateOptions(diagnosticList).find(
                        diagnostic => diagnostic.value === value,
                    ).label || '-'
                break
            default:
                content = value
                break
        }

        return content
    }

    const dataListFilter = useMemo(() => {
        if (!!dataListWithIdx && dataListWithIdx?.length > 0) {
            return dataListWithIdx.filter(
                item => !excludeIdxRows.value?.includes(item?.index),
            )
        }

        return []
    }, [dataListWithIdx, excludeIdxRows.value])

    const type = useSignal('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const excludeKeys = ['pathways', 'action', 'id', 'addressId']
    const location = useLocation()
    const handleHideForm = useCallback(() => {
        unblockOverflownScroll()
        visible.value = false
        excludeIdxRows.value = []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const saveAsExcelOrCsvFile = (type, buffer, fileName) => {
        import('file-saver').then(module => {
            if (module && module.default) {
                let TYPE = ''

                if (type === 'csv') {
                    TYPE = 'text/csv'
                } else if (type === 'xlsx') {
                    TYPE =
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
                }
                let EXTENSION = `.${type}`
                const data = new Blob([buffer], {
                    type: TYPE,
                })
                module.default.saveAs(
                    data,
                    fileName + '_export_' + new Date().getTime() + EXTENSION,
                )
            }
        })
    }

    const renderPdfPatients = () => {
        const excludePatient = index => {
            let excludeList = [...excludeIdxRows.value]

            excludeList.push(index)

            excludeIdxRows.value = [...excludeList]
        }

        const htmlElements = []
        if (!!dataListFilter && dataListFilter?.length > 0) {
            for (let i = 0; i < dataListFilter?.length; i++) {
                htmlElements.push(
                    <div
                        key={i}
                        style={{
                            pageBreakBefore: `${i === 0 ? 'avoid' : 'always'}`, // Break page after page 1
                        }}
                        className="content border-bottom-2 border-700 shadow relative mb-3 py-2">
                        <PdfContent
                            index={dataListFilter[i]?.index + 1}
                            exportData={dataListFilter[i]}
                            basicPatient={true}
                        />
                        <div className="absolute top-0 right-0 no-print">
                            <Button
                                icon="pi pi-trash"
                                severity="danger"
                                className="p-button-rounded p-button-danger p-button-outlined"
                                onClick={() => {
                                    if (dataListFilter?.length > 0) {
                                        if (dataListFilter?.length === 1) {
                                            handleHideForm()
                                        } else {
                                            excludePatient(
                                                dataListFilter[i]?.index,
                                            )
                                        }
                                    }
                                }}
                                tooltip={translateKeys('Remove patient')}
                                tooltipOptions={{position: 'left'}}
                            />
                        </div>
                    </div>,
                )
            }
        }

        return htmlElements
    }

    const automaticWidthSettingExcel = data => {
        let objectMaxLength = []
        if (data?.length > 0) {
            for (let i = 0; i < data.length; i++) {
                let value = Object.values(data[i])
                let key = Object.keys(data[i])

                if (value?.length > 0) {
                    for (let j = 0; j < value.length; j++) {
                        let maxLength = 10

                        if (typeof value[j] == 'number' || !value[j]) {
                            objectMaxLength[j] = key[j].length + 2 || maxLength
                        } else {
                            if (value[j]?.length >= key[j].length) {
                                maxLength = value[j]?.length
                            } else {
                                if (key[j] && convertKeyToLabel(key[j])) {
                                    maxLength = convertKeyToLabel(
                                        key[j],
                                    )?.length
                                }
                            }

                            if (
                                !objectMaxLength[j] ||
                                maxLength >= objectMaxLength[j]
                            ) {
                                objectMaxLength[j] = maxLength + 2
                            }
                        }
                    }
                }
            }
            if (objectMaxLength?.length > 0) {
                let wscols = objectMaxLength.map(item => {
                    return {width: item}
                })

                return wscols
            }

            return []
        } else {
            return []
        }
    }

    const includeColumnAdvance = useSignal(
        advancedOptions && advancedOptions?.length > 0
            ? [...advancedOptions?.map(item => item?.key)]
            : [],
    )

    const advanceOptionsFilter = useMemo(() => {
        let tmpExportList = [...columnList]
        let filteredExportList = tmpExportList?.filter(
            x =>
                !excludeKeys.includes(x.key) &&
                includeColumnAdvance.value.includes(x.key),
        )

        filteredExportList.forEach(item => {
            item['label'] =
                item.key === 'tas' || item.key === 'hcp'
                    ? item.key.toUpperCase()
                    : convertKeyToLabel(item.key)
        })

        return filteredExportList
    }, [columnList, excludeKeys, includeColumnAdvance.value])

    const onExport = async () => {
        let holderDataExport = dataListFilter.map(item => {
            delete item?.index
            return item
        })

        // Filter data columns
        let tempDataColumns =
            holderDataExport && holderDataExport?.length > 0
                ? [...holderDataExport]
                : []
        let filteredColumnList = []

        if (tempDataColumns.length > 0) {
            for (let i = 0; i < tempDataColumns?.length; i++) {
                filteredColumnList.push(
                    Object.keys(tempDataColumns[i])
                        .filter(key => {
                            let result = advanceOptionsFilter.find(
                                item => item?.key === key,
                            )

                            return !!result
                        })
                        .reduce((obj, key) => {
                            obj[key] =
                                convertDataToExport(
                                    key,
                                    tempDataColumns[i][key],
                                ) || '-'
                            return obj
                        }, {}),
                )
            }
        }

        const headerFormat = []

        if (filteredColumnList && filteredColumnList?.length > 0) {
            for (const key of Object.keys(filteredColumnList[0])) {
                headerFormat.push(convertKeyToLabel(key))
            }
        }

        if (type.value === types.excel || type.value === types.csv) {
            import('xlsx').then(xlsx => {
                const worksheet = xlsx.utils.json_to_sheet([])

                let headerText = `Exported at ${moment().toString()}`
                let headerRow = {A1: {t: 's', v: headerText}}

                Object.assign(worksheet, headerRow)

                xlsx.utils.sheet_add_aoa(worksheet, [headerFormat], {
                    origin: 'A3',
                })

                xlsx.utils.sheet_add_json(worksheet, filteredColumnList, {
                    origin: 'A4',
                    skipHeader: true,
                })

                worksheet['!cols'] =
                    automaticWidthSettingExcel(filteredColumnList)

                const workbook = {
                    Sheets: {data: worksheet},
                    SheetNames: ['data'],
                }

                const excelBuffer = xlsx.write(workbook, {
                    bookType: 'xlsx',
                    type: 'array',
                })

                saveAsExcelOrCsvFile(
                    type.value === types.excel ? 'xlsx' : 'csv',
                    excelBuffer,
                    location.pathname.includes('/generalPractitioner')
                        ? `GeneralPractitionerTable_${moment().toString()}`
                        : !auditLog
                        ? `ExportTable_${moment().toString()}`
                        : `PatientTable_${moment().toString()}`,
                )
            })
        }

        if (type.value === types.pdf) {
            if (location.pathname.includes('/patients')) {
                // Hide element not print
                const hideElements = document.getElementsByClassName('no-print')

                if (hideElements && hideElements?.length > 0) {
                    for (let i = 0; i < hideElements?.length; i++) {
                        hideElements[i].setAttribute('style', 'display:none;')
                    }
                }

                // Print
                const element = document.getElementById('pdf-content')

                const opt = {
                    filename: `PatientReport_${moment().toString()}.pdf`,
                    image: {type: 'jpeg', quality: 0.98},
                    html2canvas: {useCORS: true},
                    jsPDF: {
                        unit: 'in',
                        format: 'a4',
                        orientation: 'portrait',
                    },
                }

                await html2pdf().set(opt).from(element).save()

                // Show elements that hided again
                if (hideElements && hideElements?.length > 0) {
                    for (let i = 0; i < hideElements?.length; i++) {
                        hideElements[i].setAttribute('style', 'display:block;')
                    }
                }
            } else {
                import('jspdf').then(jsPDF => {
                    import('jspdf-autotable').then(() => {
                        const doc = new jsPDF.default(0, 0)

                        doc.text(`General Practitioner Table`, 14, 16)
                        doc.text(
                            `Export in ${moment().toLocaleString()}`,
                            14,
                            24,
                        )

                        let exportColsMapped = [
                            ...advanceOptionsFilter.filter(
                                col => col.key !== 'action',
                            ),
                        ]

                        exportColsMapped.forEach(item => {
                            item['field'] = convertKeyToLabel(item?.key)
                            item['header'] = convertKeyToLabel(item?.key)
                        })

                        doc.autoTable(exportColsMapped, holderDataExport, {
                            theme: 'striped',
                            startY: 28,
                            horizontalPageBreakBehaviour: 'afterAllRows',
                            horizontalPageBreak: true,
                            showHead: 'everyPage',
                            rowPageBreak: 'auto',
                            pageBreak: 'auto',
                        })
                        doc.save(
                            `GeneralPractitionerTable_${moment().toString()}.pdf`,
                        )
                    })
                })
            }
        }

        if (!auditLog) {
            await createExportLog(getUserId(), {
                importExportLogType: location.pathname.includes('/patients')
                    ? 'patient'
                    : 'general_practitioner',
            })
        }
    }

    const columnOptionsFilter = useMemo(() => {
        let tmpColumnList = [...columnList]
        let filteredColumnList = tmpColumnList?.filter(
            x => !excludeKeys.includes(x.key),
        )

        filteredColumnList.forEach(item => {
            item['label'] =
                item.key === 'tas' || item.key === 'hcp'
                    ? item.key.toUpperCase()
                    : translateDatatable(convertKeyToLabel(item.key))
        })

        return filteredColumnList
    }, [columnList, excludeKeys, translateDatatable])

    const editColumnModalSheetRef = useRef(null)
    const renderHeader = () => {
        return (
            <div className="flex flex-wrap justify-content-between align-items-center md:p-3 lg:p-3 p-0">
                <div className="flex gap-2 justify-content-between mb-3">
                    <div className="flex flex-column gap-2">
                        <h3 className="pt-2 mb-0  font-bold text-3xl md:text-4xl text-800">
                            {translateDatatable('Export Data Table')}
                        </h3>
                        <p className="text-700 text-md font-normal w-full">
                            {translateDatatable(
                                'The export table represents the data you are about to export to the selected data file type. Please check the necessary data columns for the table data before printing to the file.',
                            )}
                        </p>
                    </div>
                </div>
                <div className="block md:hidden">
                    <Button
                        label={translateDatatable('Edit Column')}
                        onClick={() => editColumnModalSheetRef.current.onShow()}
                    />
                </div>
            </div>
        )
    }

    const onDelete = idx => {
        excludeIdxRows.value = [...excludeIdxRows.value, idx]
    }

    const handleOnClick = value => {
        if (
            type.value === types.pdf &&
            location.pathname.includes('/patients')
        ) {
            renderPdfPatients()
        }

        blockOverflownScroll()
        visible.value = true
        type.value = value
    }

    const renderEditColumn = () => {
        return (
            <div className="w-full">
                <div className="flex flex-column gap-1 ">
                    {columnOptionsFilter.map((item, idx) => {
                        let isIncludes = includeColumnAdvance.value.includes(
                            item.key,
                        )
                        return (
                            <Button
                                key={idx}
                                className={`w-full justify-content-between text-left font-bold ${
                                    !isIncludes &&
                                    'bg-white text-green hover:bg-green-50 '
                                }`}
                                onClick={() => {
                                    let value = []
                                    if (isIncludes) {
                                        value =
                                            includeColumnAdvance.value.filter(
                                                x => x !== item.key,
                                            )
                                    } else {
                                        value = [
                                            ...includeColumnAdvance.value,
                                            item.key,
                                        ]
                                    }
                                    includeColumnAdvance.value = value
                                }}>
                                {' '}
                                <span className="">{item.label}</span>
                                {isIncludes && (
                                    <i className={`pi pi-check-circle`}></i>
                                )}
                            </Button>
                        )
                    })}
                </div>
            </div>
        )
    }
    const disable = useMemo(() => {
        let isDisable = false
        if (!isArrayAndNotEmpty(dataList)) {
            isDisable = true
        }
        return isDisable
    }, [dataList])

    const renderColumns = () => {
        let cols = []

        for (let i = 0; i < advanceOptionsFilter?.length; i++) {
            const key = advanceOptionsFilter[i].key
            const label = advanceOptionsFilter[i].label

            cols.push(
                <Column
                    key={key}
                    field={key}
                    body={rowData => {
                        return (
                            <div
                                className="overflow-dot overflow-text-2"
                                style={{
                                    width: '100%',
                                }}>
                                {convertDataToExport(key, rowData[key])}
                            </div>
                        )
                    }}
                    header={translateDatatable(label)}></Column>,
            )
        }

        return cols
    }

    return (
        <div>
            <div className="flex gap-1 w-full">
                <div className={auditLog ? 'w-6' : 'w-4'}>
                    <Button
                        className="p-button-info w-full"
                        label={translateDatatable('CSV')}
                        data-pr-tooltip={translateDatatable('CSV')}
                        style={{height: '2.8rem'}}
                        disabled={disable}
                        onClick={() => handleOnClick(types.csv)}
                        icon="pi pi-file"
                    />
                </div>
                <div className={auditLog ? 'w-6' : 'w-4'}>
                    <Button
                        className="p-button-success w-full  "
                        label={translateDatatable('Excel')}
                        data-pr-tooltip={translateDatatable('XLS')}
                        style={{height: '2.8rem'}}
                        disabled={disable}
                        onClick={() => handleOnClick(types.excel)}
                        icon="pi pi-file-excel"
                    />
                </div>
                {!auditLog && (
                    <div className="w-4">
                        <Button
                            className="p-button-warning w-full"
                            label={translateDatatable('PDF')}
                            data-pr-tooltip={translateDatatable('PDF')}
                            style={{height: '2.8rem'}}
                            disabled={disable}
                            onClick={() => handleOnClick(types.pdf)}
                            icon="pi pi-file-pdf"
                        />
                    </div>
                )}
            </div>
            {queryTableKey === QueryTablePatientKey && disable && (
                <Message
                    className="mt-2 w-full m-0"
                    severity="warn"
                    text={translateDatatable(
                        'You need to select a patient before exporting',
                    )}
                />
            )}
            <Sidebar
                visible={visible.value}
                onHide={handleHideForm}
                blockScroll
                fullScreen>
                <PageContentWrapper
                    className={' w-full h-full md:p-4 lg:m-4 m-0'}>
                    <FormHeader
                        header={`${translateDatatable(
                            'EXPORT',
                        )} ${type.value.toUpperCase()}`}
                        onHide={() => handleHideForm()}
                    />
                    <hr />
                    <div className="col-12">
                        {type.value === types.pdf &&
                        location.pathname.includes('/patients') ? (
                            <div className="w-full">
                                <div id="pdf-content" className="w-full">
                                    {renderPdfPatients()}
                                </div>
                                <div className="w-full mt-3">
                                    <Button
                                        className="w-full"
                                        label={translateDatatable(
                                            'Export',
                                        ).toUpperCase()}
                                        icon={'pi pi-print'}
                                        onClick={() => onExport()}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="w-full">
                                <div className="flex gap-2">
                                    <DataTable
                                        header={renderHeader}
                                        className="m-0 w-full md:w-10"
                                        value={dataListFilter}
                                        stripedRows
                                        ref={dataTableRef}
                                        responsiveLayout="scroll"
                                        scrollHeight="60vh"
                                        style={{height: '70vh'}}>
                                        {renderColumns()}
                                        <Column
                                            header={translateDatatable(
                                                'Action',
                                            )}
                                            body={rowData => (
                                                <Button
                                                    className="border-circle border-none"
                                                    style={{
                                                        backgroundColor:
                                                            '#EF4040',
                                                    }}
                                                    icon="pi pi-times"
                                                    aria-label="Cancel"
                                                    onClick={() =>
                                                        onDelete(rowData?.index)
                                                    }></Button>
                                            )}
                                        />
                                    </DataTable>
                                    <div
                                        className="hidden md:block w-2 border-round-lg p-3 text-900"
                                        style={{
                                            height: '70vh',
                                            background: '#f9f9f9',
                                        }}>
                                        <div className="col-12 flex align-items-center p-0 font-bold">
                                            <img
                                                src={EditColumn}
                                                style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    marginRight: '8px',
                                                }}
                                                alt={translateCommons(
                                                    'Edit Columns',
                                                )}
                                            />
                                            <h4 className="font-bold m-0">
                                                {translateCommons(
                                                    'Edit Columns',
                                                )}
                                            </h4>
                                        </div>
                                        <hr />
                                        <div
                                            className="overflow-scroll py-3"
                                            style={{height: '64vh'}}>
                                            {renderEditColumn()}
                                        </div>
                                    </div>
                                </div>

                                <hr />
                                <div className="col-12 py-3">
                                    <Button
                                        disabled={
                                            !dataListFilter ||
                                            dataListFilter?.length < 1
                                        }
                                        className="col-12 p-button-lg font-bold"
                                        label={translateDatatable('EXPORT')}
                                        icon={'pi pi-print'}
                                        onClick={() => onExport()}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </PageContentWrapper>

                <ToolBoxWrapper
                    zIndex={1200}
                    header={translateDatatable('Edit Columns')}
                    ref={editColumnModalSheetRef}>
                    <div
                        className="overflow-scroll py-3"
                        style={{height: '64vh'}}>
                        {renderEditColumn()}{' '}
                    </div>
                </ToolBoxWrapper>
            </Sidebar>
        </div>
    )
}

export default ExportForm
